import type { RequestType } from '@/types'

export const REQUEST_FIELDS = [
    'name',
    'email',
    'phone',
    'subject',
    'service',
    'requested_date',
    'guests',
    'location',
    'budget',
    'item',
    'quantity',
    'country',
    'recipient_name',
    'recipient_phone',
    'currency',
    'message'
] as const

export type RequestField = typeof REQUEST_FIELDS[number]

export type RequestValues = Record<RequestField, string>

export type RequestErrors = Partial<Record<RequestField, string>>

export const REQUEST_TYPES: Array<RequestType> = ['booking', 'diaspora', 'order', 'menu', 'enquiry']

export const MAX_LENGTHS: Record<RequestField, number> = {
    name: 120,
    email: 160,
    phone: 40,
    subject: 120,
    service: 160,
    requested_date: 10,
    guests: 60,
    location: 400,
    budget: 80,
    item: 160,
    quantity: 120,
    country: 80,
    recipient_name: 120,
    recipient_phone: 40,
    currency: 3,
    message: 2000
}

export const REQUIRED_FIELDS: Record<RequestType, Array<RequestField>> = {
    booking: ['name', 'phone', 'email', 'service', 'requested_date', 'guests', 'location'],
    diaspora: ['name', 'email', 'phone', 'country', 'item', 'requested_date', 'currency', 'recipient_name', 'recipient_phone', 'location'],
    order: ['name', 'phone', 'item', 'quantity', 'requested_date', 'location'],
    menu: ['name', 'email'],
    enquiry: ['name', 'email', 'subject', 'message']
}

const REQUIRED_MESSAGES: Partial<Record<RequestField, string>> = {
    name: 'Please enter your full name.',
    email: 'Please enter your email address.',
    phone: 'Please enter a phone or WhatsApp number we can reach you on.',
    subject: 'Please choose what your enquiry is about.',
    service: 'Please choose the type of event or service.',
    requested_date: 'Please choose a date.',
    guests: 'Please tell us roughly how many guests to expect.',
    location: 'Please tell us where the food should go.',
    item: 'Please choose what you would like to order.',
    quantity: 'Please tell us how much you need.',
    country: 'Please choose the country you live in.',
    recipient_name: 'Please enter the name of the person receiving the order.',
    recipient_phone: 'Please enter a phone number for the person receiving the order.',
    currency: 'Please choose the currency you would like to pay in.',
    message: 'Please write a short message.'
}

const EMAIL_PATTERN = /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[^\s@<>()[\],;:"]{2,}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const CURRENCIES = ['NGN', 'USD', 'GBP']

export const emptyRequestValues = (overrides: Partial<RequestValues> = {}): RequestValues => {
    const values = Object.fromEntries(REQUEST_FIELDS.map(field => [field, ''])) as RequestValues

    return { ...values, ...overrides }
}

const isValidPhone = (value: string): boolean => {
    if (!/^[+\d\s().-]+$/.test(value)) return false

    const digits = value.replace(/\D/g, '')

    return digits.length >= 7 && digits.length <= 15
}

// The earliest date accepted, one day before today in UTC, so visitors in any time zone can pick their own today.
const earliestDate = (): string => {
    const date = new Date(Date.now() - 24 * 60 * 60 * 1000)

    return date.toISOString().slice(0, 10)
}

export const validateRequest = (type: RequestType, values: RequestValues): RequestErrors => {
    const errors: RequestErrors = {}

    for (const field of REQUIRED_FIELDS[type]) {
        if (!values[field].trim()) errors[field] = REQUIRED_MESSAGES[field] ?? 'This field is required.'
    }

    for (const field of REQUEST_FIELDS) {
        if (!errors[field] && values[field].length > MAX_LENGTHS[field]) {
            errors[field] = `Please keep this under ${MAX_LENGTHS[field]} characters.`
        }
    }

    if (!errors.email && values.email.trim() && !EMAIL_PATTERN.test(values.email.trim())) {
        errors.email = 'Please enter a valid email address.'
    }

    if (!errors.phone && values.phone.trim() && !isValidPhone(values.phone.trim())) {
        errors.phone = 'Please enter a valid phone number, including the country code if you are abroad.'
    }

    if (!errors.recipient_phone && values.recipient_phone.trim() && !isValidPhone(values.recipient_phone.trim())) {
        errors.recipient_phone = 'Please enter a valid phone number for the recipient.'
    }

    if (!errors.requested_date && values.requested_date.trim()) {
        const date = values.requested_date.trim()

        if (!DATE_PATTERN.test(date) || Number.isNaN(Date.parse(date))) errors.requested_date = 'Please choose a valid date.'
        else if (date < earliestDate()) errors.requested_date = 'Please choose a date from today onwards.'
    }

    if (!errors.currency && values.currency.trim() && !CURRENCIES.includes(values.currency.trim())) {
        errors.currency = 'Please choose Naira, US Dollar or British Pound.'
    }

    return errors
}
